import { useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate, useLocation } from "react-router-dom";
import LayersIcon from "../assets/images/server_rgb_red.svg";
import UploadIcon from "../assets/images/upload_file_rgb_red.svg";
import AdministrationIcon from "../assets/images/two_gears_rgb_red.svg";
import ScheduledChangesIcon from "../assets/images/timetable_rgb_red.svg";
import LocationIcon from "../assets/images/location_pin_rgb_red.svg";
import ReportIcon from "../assets/images/emerging_market_solutions_rgb_red.svg";
import DashboardIcon from "../assets/images/carbon_dashboard_rgb_red.svg";
import MapIcon from "../assets/images/live_tracking_rgb_red.svg";
import DbLockButton from "../components/DbLockButton";
import { useEffect, useCallback } from "react";
import { getConfig } from "../services/configApi";
import "../assets/styles/main.scss";

const menuItems = [
  { icon: DashboardIcon, alt: "dashboard", label: "Dashboard", path: "/" },
  {
    icon: LocationIcon,
    alt: "addresses",
    label: "Warstwa Adresowa",
    path: "/addresses",
  },
  {
    icon: LocationIcon,
    alt: "postcodes",
    label: "Kody Pocztowe",
    path: "/postcodes",
  },
  { icon: MapIcon, alt: "map", label: "Mapa", path: "/map" },
  { icon: LayersIcon, alt: "layers", label: "Warstwy", path: "/layers" },
  // { icon: WaitingRoomIcon, alt: "waiting-room", label: "Poczekalnia", path: "/waiting-room" },
  {
    icon: ScheduledChangesIcon,
    alt: "scheduled-changes",
    label: "Zaplanowane zmiany",
    path: "/scheduled-changes",
  },
  {
    icon: UploadIcon,
    alt: "data-import",
    label: "Import danych",
    path: "/data-import",
  },
  { icon: ReportIcon, alt: "reports", label: "Raporty", path: "/reports" },
];

const userSubItems = [
  { label: "Lista nowych adresów", path: "/administration/new-addresses" },
  { label: "Dodaj nowy adres", path: "/administration/new-addresses/add" },
];

const adminSubItems = [
  { label: "Oddziały DHL", path: "/administration/dhl-branches" },
  { label: "Przepnij kody pocztowe", path: "/administration/branch-change" },
  { label: "Konfigurator przedziałów", path: "/administration/new-scopes/add" },
  { label: "Lista nowych adresów", path: "/administration/new-addresses" },
  { label: "Dodaj nowy adres", path: "/administration/new-addresses/add" },
  { label: "Historia zmian", path: "/administration/information-history" },
  { label: "Dziennik logów", path: "/administration/logs" },
  { label: "Użytkownicy", path: "/administration/users" },
];

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { instance, accounts } = useMsal();

  const activeAccount = instance.getActiveAccount() || accounts?.[0];

  const roles = useMemo(() => {
    const claims = activeAccount?.idTokenClaims || {};
    const raw = claims.roles || claims.role || claims.groups;
    if (!raw) return [];
    return Array.isArray(raw) ? raw : [raw];
  }, [activeAccount]);

  const canSeeAdmin = roles.includes("ROLE_SUPERUSER");

  // DB lock state for superuser
  const [dbLocked, setDbLocked] = useState(false);

  const fetchDbLockStatus = useCallback(async () => {
    try {
      const resp = await getConfig();
      setDbLocked(!!resp?.data?.isDbLocked);
    } catch {
      setDbLocked(false);
    }
  }, []);

  useEffect(() => {
    if (canSeeAdmin) {
      fetchDbLockStatus();
    }
  }, [canSeeAdmin, fetchDbLockStatus]);
  const canSeeUserAdministration = roles.includes("ROLE_USER");
  const canSeeAdministration = canSeeAdmin || canSeeUserAdministration;
  const administrationSubItems = canSeeAdmin ? adminSubItems : userSubItems;

  const [adminOpen, setAdminOpen] = useState(() => {
    const saved = localStorage.getItem("adminMenuOpen");
    return saved === "true";
  });

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const handleNavigate = (path) => navigate(path);

  const handleAdminToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAdminOpen((prev) => {
      const next = !prev;
      localStorage.setItem("adminMenuOpen", String(next));
      return next;
    });
  };

  const handleAdminClick = () => {
    if (!isOpen) {
      toggleSidebar();
      setAdminOpen(true);
      localStorage.setItem("adminMenuOpen", "true");
    }
  };

  return (
    <nav className={`sb ${isOpen ? "sb--open" : "sb--closed"}`}>
      {/* ── Toggle handle ── */}
      <button
        className="sb__toggle"
        onClick={toggleSidebar}
        aria-label={isOpen ? "Zwiń menu" : "Rozwiń menu"}
      >
        <span className="sb__toggle-bar" />
        <span className="sb__toggle-bar" />
        <span className="sb__toggle-bar" />
      </button>

      {/* ── Brand area ── */}
      <div className="sb__brand">
        <span className="sb__brand-dot" />
        <div className="sb__brand-text">
          <h2 className="sb__brand-title">Menu</h2>
          <p className="sb__brand-sub">Nawigacja</p>
        </div>
      </div>

      {/* ── Divider ── */}
      <div className="sb__divider" />

      {/* ── Navigation ── */}
      <div className="sb__scroll">
        <ul className="sb__list">
          {menuItems.map((item) => (
            <li
              key={item.path}
              className={`sb__item${isActive(item.path) ? " sb__item--active" : ""}`}
              data-tooltip={item.label}
              onClick={() => handleNavigate(item.path)}
            >
              <span className="sb__icon">
                <img src={item.icon} alt={item.alt} />
              </span>
              <span className="sb__label">{item.label}</span>
              {isActive(item.path) && <span className="sb__active-pip" />}
            </li>
          ))}

          {canSeeAdministration && (
            <>
              {/* ── Admin section ── */}
              <li className="sb__section-title">
                <span className="sb__section-line" />
                <span className="sb__section-text">Admin</span>
                <span className="sb__section-line" />
              </li>

              <li
                className={`sb__item sb__item--admin${
                  location.pathname.startsWith("/administration")
                    ? " sb__item--active"
                    : ""
                }`}
                data-tooltip="Administracja"
                onClick={isOpen ? handleAdminToggle : handleAdminClick}
              >
                <span className="sb__icon">
                  <img src={AdministrationIcon} alt="administration" />
                </span>
                <span className="sb__label">Administracja</span>
                <span
                  className={`sb__chevron${adminOpen ? " sb__chevron--open" : ""}`}
                />
              </li>

              {isOpen && adminOpen && (
                <ul className="sb__sublist">
                  {administrationSubItems.map((sub) => (
                    <li
                      key={sub.path}
                      className={`sb__subitem${isActive(sub.path) ? " sb__subitem--active" : ""}`}
                      onClick={() => handleNavigate(sub.path)}
                    >
                      <span className="sb__subitem-dot" />
                      <span className="sb__subitem-label">{sub.label}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          <DbLockButton
            canSeeAdmin={canSeeAdmin}
            isOpen={isOpen}
            dbLocked={dbLocked}
            onDbLockChange={setDbLocked}
          />
        </ul>
      </div>
    </nav>
  );
};

export default Sidebar;
